import type { AskDynamoThoughtFrequency } from "~/data/askDynamoThoughtFrequencies"
import type { DogPortraitInfo } from "~/api/auth"

const API_BASE = "/api/v1"

export interface AskDynamoUsage {
  daily_limit: number | null
  daily_remaining: number | null
}

export interface AskDynamoDog {
  name: string
  sex?: "male" | "female"
  archetype_label: string | null
  archetype_icon: string | null
  portrait: DogPortraitInfo
}

export interface AskDynamoSelectedDog {
  id: number
  name: string
  sex: "male" | "female"
  archetype_label: string | null
}

export interface AskDynamoMessage {
  id: number
  role: "user" | "assistant"
  content: string
  dog_profile_ids?: number[]
  created_at: string
}

export interface AskDynamoSuggestion {
  icon: "brain" | "heart" | "sparkles"
  text: string
  hint: string | null
}


export interface AskDynamoThoughtPreference {
  frequency: AskDynamoThoughtFrequency
  enabled: boolean
  onboarding_completed_at: string | null
}

export interface AskDynamoSessionResponse {
  dog: AskDynamoDog
  dogs?: AskDynamoSelectedDog[]
  dog_profile_ids?: number[]
  usage: AskDynamoUsage
  messages: AskDynamoMessage[]
  suggestions: AskDynamoSuggestion[]
  thought_preference: AskDynamoThoughtPreference | null
}

export interface AskDynamoMessageResponse {
  assistant_message: AskDynamoMessage
  usage: AskDynamoUsage
  messages: AskDynamoMessage[]
  daily_limit_exceeded: boolean
}

export class AskDynamoApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly dailyLimitExceeded = false,
  ) {
    super(message)
    this.name = "AskDynamoApiError"
  }
}

export async function getAskDynamoSession(
  dogProfileIds?: number[],
): Promise<AskDynamoSessionResponse> {
  const params = new URLSearchParams()
  ;(dogProfileIds ?? []).forEach((id) => params.append("dog_profile_ids[]", String(id)))
  const query = params.toString()
  const res = await fetch(`${API_BASE}/ask_dynamo${query ? `?${query}` : ""}`, {
    credentials: "include",
  })
  if (!res.ok) throw new Error("Not authenticated")
  return res.json()
}

export async function postAskDynamoMessage(
  content: string,
  dogProfileIds?: number[],
): Promise<AskDynamoMessageResponse> {
  const res = await fetch(`${API_BASE}/ask_dynamo/messages`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      content,
      dog_profile_ids: dogProfileIds ?? [],
    }),
  })

  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const message = Array.isArray(json.errors) ? json.errors[0] : "Could not send message"
    throw new AskDynamoApiError(message, res.status, json.daily_limit_exceeded === true)
  }

  return json
}

export interface SampleThought {
  text: string
  sequence_number: number
}

export async function getSampleThoughts(
  count = 3,
  exclude: number[] = [],
  dogProfileIds?: number[],
): Promise<SampleThought[]> {
  const params = new URLSearchParams({ count: String(count) })
  exclude.forEach((sequenceNumber) => params.append("exclude[]", String(sequenceNumber)))
  ;(dogProfileIds ?? []).forEach((id) => params.append("dog_profile_ids[]", String(id)))

  const res = await fetch(`${API_BASE}/ask_dynamo/sample_thoughts?${params}`, {
    credentials: "include",
  })
  if (!res.ok) throw new Error("Could not load sample thoughts")
  const json = (await res.json()) as { thoughts: SampleThought[] }
  return json.thoughts
}
